import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ProgressBarComponent } from '@app/shared/ui/simple-components/progress-bar/progress-bar.component';
import { take } from 'rxjs/operators';

export interface ProgressPeak {
  position: number; // percentage
  time: number; // ms
  preLast?: boolean;
}

@Component({
  standalone: false,
  selector: 'ti-progress-dialog',
  templateUrl: 'progress-dialog.component.html',
  styleUrls: ['progress-dialog.component.sass'],
})
export class ProgressDialogComponent implements OnInit {
  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  readonly strings = tiStrings.app.misc;
  readonly localStorageKeys = LocalStorageKeys;

  @Input() isLoading: boolean = false;

  @Output() close = new EventEmitter<any>();
  @Output() onStart = new EventEmitter();
  @Output() onComplete = new EventEmitter();
  maxWidth: number = 0;

  value: string = '';
  title: string = '';
  isFieldInvalid: boolean;
  isIndeterminate: boolean = false;
  simulateProcess: boolean = false;
  defaultPreLastPercentage: number = 75;
  defaultPreLastThresholdAmountPercentage: number = 20; // +-N % of defaultPreLastPercentage
  randomPreLastPercentage: number;
  randomizePeaksCount: boolean = true;
  peakNominalDistance: number = 10; //
  randomizePeakThresholdAmountPercentage: number = 70; // +-N % of peakNominalDistance
  //peaksCount: boolean = true;
  randomizePeaksTimings: boolean = true;
  peaksBaseTime: number = 1000; // ms
  peaksBaseTimeThresholdAmount: number = 70; // +-N % of peaksBaseTime
  randomizePreLastPosition: boolean = true;
  peaks: ProgressPeak[] = []; // predefined peaks are welcome, todo separate mode for this
  currentTimeoutId: number;
  @ViewChild(ProgressBarComponent) private progressBarComponent: ProgressBarComponent;

  ngOnInit(): void {
    if (!this.isIndeterminate && this.simulateProcess) {
      this.onComplete.pipe(take(1)).subscribe((result) => {
        this.forceComplete();
      });
      this.createPeaks(this.peaks);
      this.simulateProgress();
    }
  }

  simulateProgress() {
    if (this.peaks?.length) {
      const peak = this.peaks.shift();
      if (peak) {
        this.currentTimeoutId = setTimeout((_) => {
          this.progressBarComponent.value = peak.position;
          this.simulateProgress();
        }, peak.time);
      }
    }
  }

  forceComplete() {
    this.currentTimeoutId && clearInterval(this.currentTimeoutId);
    this.peaks = [];
    this.progressBarComponent.value = 100;
  }

  createPeaks(peaks?: ProgressPeak[]) {
    peaks = peaks || this.peaks;
    if (peaks?.length === 0) {
      peaks.push(this.generatePeak());
    } else {
      const currentPeak = peaks[peaks.length - 1];
      if (currentPeak.preLast) {
        return peaks;
      } else {
        peaks.push(this.generatePeak(currentPeak));
      }
    }
    return this.createPeaks(peaks);
  }

  generatePeak(previousPeak?: ProgressPeak): ProgressPeak {
    let position = 0;
    let time = 0;
    if (previousPeak) {
      const direction = Math.random() < 0.5 ? -1 : 1;
      const directionTime = Math.random() < 0.5 ? -1 : 1;
      const minDistance =
        this.peakNominalDistance - this.peakNominalDistance * (this.randomizePeakThresholdAmountPercentage / 100);
      const maxDistance =
        this.peakNominalDistance + this.peakNominalDistance * (this.randomizePeakThresholdAmountPercentage / 100);
      const lastMinPosition =
        this.defaultPreLastPercentage -
        this.defaultPreLastPercentage * (this.defaultPreLastThresholdAmountPercentage / 100);
      const lastMaxPosition =
        this.defaultPreLastPercentage +
        this.defaultPreLastPercentage * (this.defaultPreLastThresholdAmountPercentage / 100);
      const randomDistance = Math.floor(Math.random() * (maxDistance - minDistance + 1)) + minDistance;
      this.randomPreLastPercentage =
        this.randomPreLastPercentage ||
        Math.floor(Math.random() * (lastMaxPosition - lastMinPosition + 1)) + lastMinPosition;
      this.randomPreLastPercentage = this.randomPreLastPercentage >= 100 ? 100 : this.randomPreLastPercentage;
      const timeThreshold = Math.floor(Math.random() * (Math.floor(this.peaksBaseTimeThresholdAmount) + 1));
      const newPosition = previousPeak.position + randomDistance;
      time = this.peaksBaseTime + Math.floor((direction * timeThreshold) / 100);
      if (newPosition >= this.randomPreLastPercentage) {
        return {
          position: this.randomPreLastPercentage,
          time,
          preLast: true,
        };
      } else {
        return {
          position: newPosition,
          time,
        };
      }
    }
    return {
      position,
      time,
    };
  }

  private emitClose(result: 'yes' | 'no'): void {
    this.close.emit({
      action: result,
      text: this.value,
    });
  }
}
